import React, { useEffect, useState } from "react";
import { Check, ChevronsUpDown, MapPin, Search } from "lucide-react";
import { api } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";

/**
 * Searchable combobox for Project_Station backed by the /api/substations endpoint.
 * Falls back gracefully to free-text typing if substations cannot be loaded.
 *
 * Stores and reports the substation NAME (label without city/state suffix) so
 * existing shipments using PROJECT_STATIONS string values stay compatible.
 */
export default function SubstationCombobox({
  value = "",
  onChange,
  testid = "substation-combobox",
  placeholder = "Search substation by name, city or ID…",
  required = false,
  extraOptions = [],
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api
      .get("/substations")
      .then(({ data }) => {
        if (!mounted) return;
        // Merge API rows + caller-supplied extras, dedup'ing by upper-cased
        // name/label so a station never shows twice when both sources list it.
        const seen = new Set();
        const out = [];
        const push = (row) => {
          const keys = [
            (row.label || "").trim().toUpperCase(),
            (row.name || "").trim().toUpperCase(),
          ].filter(Boolean);
          if (keys.some((k) => seen.has(k))) return;
          keys.forEach((k) => seen.add(k));
          out.push(row);
        };
        for (const r of data) push(r);
        for (const s of extraOptions) {
          push({ id: s, name: s, city: "", state: "", label: s });
        }
        setItems(out);
      })
      .catch(() => {
        if (!mounted) return;
        setItems(extraOptions.map((s) => ({ id: s, name: s, label: s, city: "", state: "" })));
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const display = value || (loading ? "Loading…" : "Select substation…");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={`${testid}-trigger`}
          className={`rg-tap w-full border-2 rounded-sm px-3 text-base bg-white text-left flex items-center justify-between gap-2 outline-none ${
            value
              ? "border-slate-300 hover:border-slate-900"
              : required
              ? "border-amber-500 hover:border-slate-900"
              : "border-slate-300 hover:border-slate-900"
          }`}
        >
          <span className="flex items-center gap-2 truncate">
            <MapPin className="h-4 w-4 text-slate-500 flex-shrink-0" />
            <span className={`truncate ${value ? "text-slate-900 font-bold" : "text-slate-500"}`}>
              {display}
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 text-slate-500 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-w-[100vw]" align="start">
        <Command shouldFilter={true} data-testid={`${testid}-command`}>
          <div className="flex items-center border-b-2 border-slate-200 px-2">
            <Search className="h-4 w-4 text-slate-500" />
            <CommandInput
              placeholder={placeholder}
              className="border-0"
              data-testid={`${testid}-input`}
            />
          </div>
          <CommandList className="max-h-72">
            <CommandEmpty>
              {loading ? "Loading…" : "No substation found. (Type a custom value above and press save.)"}
            </CommandEmpty>
            <CommandGroup heading={`${items.length} stations`}>
              {items.map((s) => {
                const selected = value === s.name || value === s.label;
                return (
                  <CommandItem
                    key={`${s.id}-${s.label}`}
                    value={s.label}
                    data-testid={`${testid}-option-${s.id}`}
                    onSelect={() => {
                      onChange(s.name);
                      setOpen(false);
                    }}
                    className="cursor-pointer"
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${selected ? "opacity-100" : "opacity-0"}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{s.name}</div>
                      {(s.city || s.state) && (
                        <div className="text-xs text-slate-500 truncate">
                          {s.city}
                          {s.city && s.state ? ", " : ""}
                          {s.state}
                          {s.id && s.id !== s.name ? ` · ${s.id}` : ""}
                        </div>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
